import { Stack } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CustomContainer } from "../../../../components/Containers";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import useDebounce from "../../../../hooks/useDebounce";
import {
  setPackageCurrentPage,
  setPackagePageLimit,
  setPackageTotalCount,
  setPackageTotalPages,
} from "../../../../lib/slices/paginationSlice";
import {
  exportBasePackages,
  getSupplierAllBasePackages,
  updateSupplierBasePackage,
} from "../../../../services/supplier/packageApiFn";
import {
  calculateVat,
  handleOpenSnackBar,
  roundTo2Decimal,
} from "../../../../utils";
import { Filters } from "./Filters";
import {
  setBasePackageId,
  setPackagePageType,
} from "../../../../lib/slices/commonSlice";
import moment from "moment";
import {
  PRODUCT_USAGE_TYPE,
  CURRENCY,
  DEFAULT_VAT_PERCENTAGE,
} from "../../../../constants/Variables";
import { CSVLink } from "react-csv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const headerData = [
  "common.createdAt",
  "common.name&Image",
  "common.solutionType",
  "common.category",
  "common.subCategory",
  "common.subSubCategory",
  "common.packagePrice",
  "common.packageEffectivePrice",
  "common.vat",
  "common.packageVatAmount",
  "common.packagePriceAfterVat",
  "common.orderAllowed",
  "common.totalOrders",
  "common.rating",
  "common.verified",
  "common.status",
  // "common.action",
];

const tableRows = [
  "created_date",
  "package_name_image",
  "package_solution_type",
  "package_category_name",
  "package_sub_category_name",
  "package_sub_sub_category_name",
  "package_price",
  "package_effective_price",
  "app_vat",
  "package_vat_amount",
  "package_price_after_vat",
  "package_order_allowed",
  "totalOrders",
  "rating_in_number",
  "package_verified",
  "package_status",
  "actions",
];

export const Package = ({ handleChangeTab }) => {
  // Translation Hook
  const { t } = useTranslation();
  // Dispatch Redux
  const dispatch = useDispatch();
  // CSV Link Ref
  const csvLinkRef = useRef(null);
  // Export Data State
  const [exportData, setExportData] = useState([]);
  // Export Loading State
  const [exportLoading, setExportLoading] = useState(false);
  // All Packages State
  const [allPackages, setAllPackages] = useState([]);
  // Loading State
  const [isLoading, setIsLoading] = useState(false);
  // Get User Data from Redux
  const userData = useSelector((state) => state.userData.user);
  // Get Active Tab from Redux
  const activeTab = useSelector((state) => state.activeTab.tabs);
  // Get Current Language from Redux
  const { currentLanguage } = useSelector((state) => state.translationReducer);
  // Get Pagination from Redux
  const pagination = useSelector((state) => state.paginationReducer);
  // Get Package Search Page from Redux
  const searchKey = useSelector((state) => state.SearchKeysReducer.searchKeys);
  // Get Setting from the Redux
  const { settings } = useSelector((state) => state.settingsReducer);
  // Get Debounced Search Term
  const debouncedSearchTerm = useDebounce(
    searchKey.supplierPackageSearchValue,
    1000,
    true,
    () => {
      dispatch(setPackageCurrentPage(1));
    },
  );

  const fetchchedAllPackages = async (
    supplierId,
    search,
    categoryId,
    subCategoryId,
    subSubCategoryId,
    page,
    limit,
  ) => {
    let query = {
      supplierId: supplierId || userData?._id,
      search: search || "",
      categoryId: categoryId === "All" ? "" : categoryId || "",
      subCategoryId: subCategoryId === "All" ? "" : subCategoryId || "",
      subSubCategoryId:
        subSubCategoryId === "All" ? "" : subSubCategoryId || "",
      page: page || 1,
      limit: limit || 20,
    };
    try {
      setIsLoading(true);
      const response = await getSupplierAllBasePackages(query);
      if (response?.data?.status === "success") {
        const packages = response?.data?.data?.data;
        const pagination = response?.data?.data?.pagination;
        // fetchchedUploadedProducts();
        setAllPackages(packages);
        dispatch(setPackagePageLimit(pagination.limit));
        dispatch(setPackageCurrentPage(pagination.page));
        dispatch(setPackageTotalCount(pagination.total));
        dispatch(setPackageTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchchedAllPackages(
        userData?._id,
        debouncedSearchTerm,
        searchKey.supplierPackageCategoryId,
        searchKey.supplierPackageSubCategoryId,
        searchKey.supplierPackageSubSubCategoryId,
        pagination.packagePage,
        pagination.packageLimit,
      );
    }
  }, [
    userData,
    debouncedSearchTerm,
    searchKey.supplierPackageCategoryId,
    searchKey.supplierPackageSubCategoryId,
    searchKey.supplierPackageSubSubCategoryId,
    pagination.packagePage,
    pagination.packageLimit,
  ]);

  const handleChangeStatus = async (e, row, key = "status") => {
    try {
      setIsLoading(true);
      const response = await updateSupplierBasePackage(row?._id, { [key]: e });
      if (response?.data?.status === "success") {
        fetchchedAllPackages(
          userData?._id,
          searchKey.supplierPackageSearchValue,
          searchKey.supplierPackageCategoryId,
          searchKey.supplierPackageSubCategoryId,
          searchKey.supplierPackageSubSubCategoryId,
          pagination.packagePage,
          pagination.packageLimit,
        );
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  // handle page change
  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setPackageCurrentPage(page));
  };

  // handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setPackagePageLimit(limit));
    dispatch(setPackageCurrentPage(1));
  };

  // Export data for base packages
  const prepareExportData = async () => {
    try {
      setExportLoading(true);
      const query = {
        supplierId: userData?._id,
      };
      const response = await exportBasePackages(query);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        const formattedData = [];
        data.forEach((item) => {
          // This is calculated for the base packages
          const finalPackagePrice = item.effectivePackagePrice;

          // This is calculated for the base packages
          const packageVat = calculateVat(
            finalPackagePrice,
            settings?.tax || DEFAULT_VAT_PERCENTAGE,
          );

          // This is calculated for the base packages
          const finalPriceAfterVat = roundTo2Decimal(
            finalPackagePrice + packageVat,
          );
          formattedData.push({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
            "Object Id": item._id || "-",
            "Slug In (Ro)": item.mlSlug?.ro || "-",
            "Slug In (En)": item.mlSlug?.en || "-",
            "Store Name": item?.createdBy?.businessName || "-",
            "Cover Image": item.coverImage || "-",
            Images: item.images?.map((image) => image).join(", ") || "-",
            "Name In (Ro)": item.name?.ro || "-",
            "Name In (En)": item.name?.en || "-",
            "Solution Type (Ro)":
              item.solutionType
                ?.map(
                  (type) =>
                    PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label?.ro,
                )
                .filter(Boolean)
                .join(", ") || "-",
            "Solution Type (En)":
              item.solutionType
                ?.map(
                  (type) =>
                    PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label?.en,
                )
                .filter(Boolean)
                .join(", ") || "-",
            "Category (Ro)": item.category?.name?.ro || "-",
            "Category (En)": item.category?.name?.en || "-",
            "Sub Category (Ro)":
              item.subCategory?.map((sub) => sub.name?.ro).join(", ") || "-",
            "Sub Category (En)":
              item.subCategory?.map((sub) => sub.name?.en).join(", ") || "-",
            "Sub Sub Category (Ro)":
              item.subSubCategory?.map((sub) => sub.name?.ro).join(", ") || "-",
            "Sub Sub Category (En)":
              item.subSubCategory?.map((sub) => sub.name?.en).join(", ") || "-",
            Price: `${item.packagePrice?.toFixed(2)} ${CURRENCY}`,
            "Effective Price": `${item.effectivePackagePrice?.toFixed(2)} ${CURRENCY}`,
            VAT: `${settings?.tax || DEFAULT_VAT_PERCENTAGE}%`,
            "VAT Amount": `${packageVat.toFixed(2)} ${CURRENCY}`,
            "Price After VAT": `${finalPriceAfterVat.toFixed(2)} ${CURRENCY}`,
            "Order Allowed": item.isAllowedOrder ? "Yes" : "No",
            "Total Orders": item.totalOrders,
            Status: item.status ? "Active" : "Inactive",
            Verified: item.isVerified ? "Yes" : "No",
            Rating: item.rating?.toFixed(0) || "-",
          });
        });

        setExportData(formattedData);

        // Data set hone ke baad manually click trigger karo
        setTimeout(() => {
          csvLinkRef.current?.link?.click();
        }, 100);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setExportLoading(false);
    }
  };

  const renderHeaderActions = () => {
    return (
      <Stack
        flexGrow={1}
        direction={{ md: "row" }}
        gap={1}
        justifyContent={"flex-end"}
        alignItems={"center"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={t("supplierProductTab.addPackage")}
          handleClickBtn={() => {
            handleChangeTab("add_edit_package");
            dispatch(setPackagePageType("add"));
            dispatch(setBasePackageId(""));
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />

        <CSVLink
          ref={csvLinkRef}
          data={exportData}
          filename={"base_packages.csv"}
          className="csv-link"
          target="_blank"
          style={{ display: "none" }}
        />

        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<FileDownloadIcon width={20} />}
          disabled={exportLoading}
          handleClickBtn={prepareExportData}
        />
      </Stack>
    );
  };

  // render table
  const renderTable = useMemo(() => {
    return (
      <DynamicPaginatedTable
        stickyActions={true}
        tableWidth={3000}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allPackages || []}
        isLoading={isLoading}
        showDynamicPagination={true}
        paginatedData={{
          page: pagination.packagePage,
          limit: pagination.packageLimit,
          total: pagination.packageTotal,
          totalPages: pagination.packageTotalPages,
        }}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onUpdateVerification={(e, row) =>
          handleChangeStatus(e, row, "isVerified")
        }
        onUpdateStatus={(e, row) => handleChangeStatus(e, row, "status")}
        onEdit={(row) => {
          handleChangeTab("add_edit_package");
          dispatch(setPackagePageType("edit"));
          dispatch(setBasePackageId(row?._id));
        }}
      />
    );
  }, [allPackages, isLoading]);

  return (
    <React.Fragment key="supplier-product-list">
      <CustomContainer
        primaryHeading={t("supplierProductTab.basePackageList")}
        secondaryHeading={t("supplierProductTab.basePackageDetailsDescription")}
        customHeaderActions={renderHeaderActions()}
        filterSection={<Filters />}
      >
        {renderTable}
      </CustomContainer>
    </React.Fragment>
  );
};
